import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react'
import { Link } from 'react-router-dom'
import { createPortal } from 'react-dom'
import { InfoTip, QuantityStepper, useCart, useNotification } from '@alwindows/shared'
import { getSpareImageSrc, type SpareColour } from '@alwindows/shared/api/spareParts'
import { QTY_UPPER_LIMIT } from './constants'
import type { Product } from './types'

type Props = {
  product: Product
  /** ColourCode → Desc lookup. Falls back to the raw code when absent. */
  colourLabels?: Map<string, string>
}

export function ProductCard({ product, colourLabels }: Props) {
  const [variantIdx, setVariantIdx] = useState(0)
  const [qty, setQty] = useState(1)
  const [added, setAdded] = useState(false)
  const { cart, addItem } = useCart()
  const { notify } = useNotification()

  const variant = product.variants[variantIdx]
  if (!variant) return null

  const handleAdd = () => {
    const existing = cart.items.find((i) => i.sku === variant.MaterialNo)
    const existingQty = existing?.qty ?? 0
    if (existingQty + qty > QTY_UPPER_LIMIT) {
      // Legacy parity: cap per-SKU quantity at QTY_UPPER_LIMIT (30) and
      // tell the user *why* nothing was added so they don't keep tapping.
      notify({
        variant: 'error',
        title: 'Quantity limit reached',
        body: (
          <p className="no-m">
            {variant.Desc} is limited to {QTY_UPPER_LIMIT} per order
            {existingQty > 0 ? ` (you already have ${existingQty} in your cart).` : '.'}
          </p>
        ),
        durationMs: 5000,
      })
      return
    }
    const imageSrc = getSpareImageSrc(variant.FullImage, variant.IsCharactImage)
    const unitPrice = parseFloat(variant.Price)
    addItem({
      sku: variant.MaterialNo,
      qty,
      price: unitPrice,
      name: variant.Desc,
      image: imageSrc,
      weight: parseFloat(variant.GrossWeight) || 0,
      prodFilter: variant.ProdFilter,
    })
    notify({
      variant: 'success',
      title: 'Added to cart',
      body: (
        <div className="al-cart-toast">
          {imageSrc ? (
            <div className="al-cart-toast__thumb">
              <img src={imageSrc} alt="" />
            </div>
          ) : null}
          <div className="al-cart-toast__meta">
            <p className="al-cart-toast__name">{variant.Desc}</p>
            <p className="al-cart-toast__line">
              Qty: {qty} · <strong>${(unitPrice * qty).toFixed(2)}</strong>
            </p>
            <Link to="/cart" className="al-cart-toast__cta">
              View cart →
            </Link>
          </div>
        </div>
      ),
    })
    setAdded(true)
    setTimeout(() => setAdded(false), 1500)
  }

  const imgSrc = getSpareImageSrc(variant.FullImage, variant.IsCharactImage)

  return (
    // Schema.org Product microdata. Marks up the currently-displayed
    // variant — name/sku/image on the outer card, Offer (price + currency +
    // availability) nested in the price row. Visible formatting stays
    // untouched; the machine-readable values live in <meta>/<link> tags,
    // which is the spec-sanctioned way to expose data without rendering it.
    <article className="al-pc" itemScope itemType="https://schema.org/Product">
      <div className="al-pc__media">
        {imgSrc ? (
          <img src={imgSrc} alt={variant.Desc} loading="lazy" itemProp="image" />
        ) : null}
      </div>
      <div className="al-pc__body">
        <div>
          <h2 className="medium no-m-t m-bx1" itemProp="name">
            {variant.Desc}
          </h2>
          <p className="xsmall al-pc__ref">
            ref.<span itemProp="sku">{variant.MaterialNo}</span>
            {/* Per-product note from SAP (legacy ProductCard InfoTip) —
                shown behind an ⓘ rather than always-on, to keep the card
                compact. Renders nothing when Info is absent. */}
            <InfoTip text={variant.Info} label={`About ${variant.Desc}`} />
          </p>
        </div>

        <div className="al-pc__footer">
          {product.variants.length > 1 ? (
            <ColourSelect
              variants={product.variants}
              value={variantIdx}
              onChange={setVariantIdx}
              colourLabels={colourLabels}
            />
          ) : null}

          <div
            className="al-pc__row"
            itemProp="offers"
            itemScope
            itemType="https://schema.org/Offer"
          >
            <meta itemProp="priceCurrency" content="AUD" />
            <meta itemProp="price" content={variant.Price} />
            <link itemProp="availability" href="https://schema.org/InStock" />
            <link itemProp="itemCondition" href="https://schema.org/NewCondition" />
            <p className="bold no-m">${parseFloat(variant.Price).toFixed(2)}</p>
            <QuantityStepper
              value={qty}
              onChange={setQty}
              label="Quantity"
              max={QTY_UPPER_LIMIT}
            />
          </div>

          <button
            type="button"
            className="button button--fill button--primary-outline button--wide"
            onClick={handleAdd}
          >
            {added ? 'Added!' : 'Add to cart'}
          </button>
        </div>
      </div>
    </article>
  )
}

/** Pure utility — exported so other catalog code can share the same lookup. */
export function buildColourLabels(colours: SpareColour[]): Map<string, string> {
  return new Map(colours.map((c) => [c.ColourCode, c.Desc]))
}

// WAI-ARIA listbox pattern. Native <select> can't render a colour swatch
// alongside each option, and the legacy app showed both — so we hand-roll the
// dropdown with role="listbox" + role="option" and full keyboard support.
type ColourSelectProps = {
  variants: Product['variants']
  value: number
  onChange: (idx: number) => void
  colourLabels?: Map<string, string>
}

type Rect = { top: number; left: number; width: number }

function ColourSelect({ variants, value, onChange, colourLabels }: ColourSelectProps) {
  const [open, setOpen] = useState(false)
  const [highlighted, setHighlighted] = useState(value)
  const [rect, setRect] = useState<Rect | null>(null)
  const listRef = useRef<HTMLUListElement | null>(null)
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const listId = useId()
  const labelId = useId()

  const labelFor = (code: string) => colourLabels?.get(code) ?? code
  const selected = variants[value]!

  // Close on outside click. With the listbox in a portal, we have to allow
  // clicks inside the trigger OR inside the (portaled) list.
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node
      if (triggerRef.current?.contains(target)) return
      if (listRef.current?.contains(target)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  // Sync the highlight to the active selection whenever the listbox opens.
  useEffect(() => {
    if (open) {
      setHighlighted(value)
      // Defer focus so the list is mounted.
      queueMicrotask(() => listRef.current?.focus())
    }
  }, [open, value])

  // Position the portaled listbox under the trigger. useLayoutEffect so the
  // first paint already has the correct coords — no flash at (0,0). Recompute
  // on scroll/resize while open; close-on-anything-else is the simpler path.
  useLayoutEffect(() => {
    if (!open) return
    const update = () => {
      const r = triggerRef.current?.getBoundingClientRect()
      if (r) setRect({ top: r.bottom + 2, left: r.left, width: r.width })
    }
    update()
    window.addEventListener('scroll', update, true)
    window.addEventListener('resize', update)
    return () => {
      window.removeEventListener('scroll', update, true)
      window.removeEventListener('resize', update)
    }
  }, [open])

  const commit = (idx: number) => {
    onChange(idx)
    setOpen(false)
    triggerRef.current?.focus()
  }

  const onListKeyDown = (e: ReactKeyboardEvent<HTMLUListElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlighted((i) => Math.min(variants.length - 1, i + 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlighted((i) => Math.max(0, i - 1))
    } else if (e.key === 'Home') {
      e.preventDefault()
      setHighlighted(0)
    } else if (e.key === 'End') {
      e.preventDefault()
      setHighlighted(variants.length - 1)
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      commit(highlighted)
    } else if (e.key === 'Escape' || e.key === 'Tab') {
      setOpen(false)
      if (e.key === 'Escape') triggerRef.current?.focus()
    }
  }

  const listbox =
    open && rect ? (
      <ul
        id={listId}
        ref={listRef}
        role="listbox"
        aria-labelledby={labelId}
        tabIndex={-1}
        className="al-pc__colour-list"
        style={{ position: 'fixed', top: rect.top, left: rect.left, width: rect.width }}
        onKeyDown={onListKeyDown}
        aria-activedescendant={`${listId}-opt-${highlighted}`}
      >
        {variants.map((v, idx) => (
          <li
            key={v.MaterialNo}
            id={`${listId}-opt-${idx}`}
            role="option"
            aria-selected={idx === value}
            className={`xxsmall al-pc__colour-option${idx === highlighted ? ' al-pc__colour-option--hl' : ''}`}
            onMouseEnter={() => setHighlighted(idx)}
            onMouseDown={(e) => {
              // mousedown so the option commits before the list's blur fires.
              e.preventDefault()
              commit(idx)
            }}
          >
            <span
              className="al-pc__swatch"
              style={{ background: v.Colour || '#fff' }}
              aria-hidden="true"
            />
            <span className="al-pc__colour-text">{labelFor(v.Colour)}</span>
          </li>
        ))}
      </ul>
    ) : null

  return (
    <div className="al-pc__colour">
      <span id={labelId} className="xxsmall">
        Colour
      </span>
      <button
        ref={triggerRef}
        type="button"
        className="al-pc__colour-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        aria-labelledby={`${labelId} ${listId}-value`}
        onClick={() => setOpen((o) => !o)}
      >
        <span
          className="al-pc__swatch"
          style={{ background: selected.Colour || '#fff' }}
          aria-hidden="true"
        />
        <span id={`${listId}-value`} className="al-pc__colour-text">
          {labelFor(selected.Colour)}
        </span>
        <span className="al-pc__colour-chev" aria-hidden="true" data-open={open}>
          <svg width="12" height="8" viewBox="0 0 12 8" fill="none">
            <path
              d="M1 1l5 5 5-5"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </button>
      {/* Portal so the dropdown doesn't push the card body content. */}
      {listbox ? createPortal(listbox, document.body) : null}
    </div>
  )
}
